import { type FC, memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { first, isNil, noop, values } from 'lodash';
// Import Types
import type { CJVisualizationRewardCondition, CJVisualizationRewardUIModel } from 'types';
// Import Components
import CJFilterCondition from 'components/CustomerJourney/forms/CJFilterCondition';
import { CustomerJourneyVisualizationTemplateGroup } from 'components/CustomerJourneyVisualization';
import CustomerJourneyVisualizationReferenceValue, {
    type CJVisualizationReferenceValueUpdate,
} from './CustomerJourneyVisualizationReferenceValue';
import CustomerJourneyVisualizationToggleSection from './CustomerJourneyVisualizationToggleSection';
// Import Helpers
import { itemsIsValid } from 'services/Helpers';
import {
    getCJVisualizationReferenceFormConfig,
    getMatchedProperties,
    mapPropertiesToCascadeDropdownModel,
} from 'services/customerJourney/helpers';
// Import Constants
import { l, LogicTypesValues } from 'constants/common';
// Import Styles
import styles from './customerJourneyVisualizationReward.module.scss';

interface Props {
    defaultValue?: Nilable<CJVisualizationRewardUIModel>;
    node?: Nullable<AnyObject>;
    elements?: Nullable<AnyObject[]>;
    option?: Nullable<AnyObject>;
    rewardClientBonusIdOption?: Nullable<AnyObject>;
    collectedProperties?: AnyObject[];
    getUpdate?: (data: Partial<CJVisualizationRewardUIModel>) => void;
}

const getDefaultCondition = (): CJVisualizationRewardCondition => ({
    logic: LogicTypesValues.and,
    filters: [],
    isGroupFilter: true,
    isValid: true,
});

const CustomerJourneyVisualizationReward: FC<Props> = (props) => {
    const {
        defaultValue = null,
        node = null,
        elements = null,
        option = null,
        rewardClientBonusIdOption = null,
        collectedProperties = [],
        getUpdate = noop,
    } = props;

    const { t } = useTranslation();

    const [showReward, setShowReward] = useState<boolean>(defaultValue?.showReward === true);
    const [templateData, setTemplateData] = useState<AnyObject>(defaultValue?.templates ?? {});
    const [condition, setCondition] = useState<CJVisualizationRewardCondition>(
        defaultValue?.condition ?? getDefaultCondition(),
    );
    const [clientBonusId, setClientBonusId] = useState<CJVisualizationReferenceValueUpdate>({
        value: defaultValue?.clientBonusId ?? null,
        isValid: false,
    });

    const clientBonusIdType: Nullable<string> = first<string>(rewardClientBonusIdOption?.BaseTypes) ?? null;

    const clientBonusIdFormConfig = useMemo(
        () => (isNil(clientBonusIdType) ? null : getCJVisualizationReferenceFormConfig(clientBonusIdType)),
        [clientBonusIdType],
    );

    const clientBonusIdProperties = useMemo(
        () =>
            isNil(clientBonusIdType)
                ? []
                : mapPropertiesToCascadeDropdownModel(getMatchedProperties(clientBonusIdType, collectedProperties)),
        [clientBonusIdType, collectedProperties],
    );

    const templateChangeHandler = (languageKey, data) => {
        setTemplateData((prev) => {
            return { ...prev, [languageKey]: { ...prev?.[languageKey], ...data } };
        });
    };

    const conditionUpdateHandler = (conditionValue) => {
        setCondition((prev) => ({ ...prev, ...conditionValue }));
    };

    useEffect(() => {
        // When reward is hidden its configuration is not validated and is not sent to the backend
        const isClientBonusIdValid = isNil(clientBonusIdFormConfig) || clientBonusId.isValid;
        const isRewardValid =
            !showReward || (itemsIsValid(values(templateData)) && condition?.isValid === true && isClientBonusIdValid);

        getUpdate({
            showReward,
            templates: templateData,
            condition,
            clientBonusId: clientBonusId.value,
            isValid: isRewardValid,
        });
        // DEPS: [getUpdate, clientBonusIdFormConfig]
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showReward, templateData, condition, clientBonusId]);

    return (
        <CustomerJourneyVisualizationToggleSection
            contentClassName={styles.rewardContent}
            label={t(l.ShowReward)}
            description={t(l.ShowRewardDescription)}
            isChecked={showReward}
            onChange={setShowReward}
        >
            <CustomerJourneyVisualizationTemplateGroup templates={templateData} onChange={templateChangeHandler} />
            {!isNil(node) && !isNil(rewardClientBonusIdOption) && !isNil(clientBonusIdFormConfig) && (
                <CustomerJourneyVisualizationReferenceValue
                    label={t(l.RewardedBonus)}
                    contentClassName={styles.rewardBonusContent}
                    formConfig={clientBonusIdFormConfig}
                    option={rewardClientBonusIdOption}
                    value={clientBonusId.value}
                    referenceProperties={clientBonusIdProperties}
                    node={node}
                    elements={elements}
                    isReferenceOnly={true}
                    getUpdate={setClientBonusId}
                />
            )}
            {!isNil(node) && !isNil(option) && (
                <CJFilterCondition
                    hasSectionLayout={true}
                    collectedProperties={collectedProperties}
                    node={node}
                    elements={elements}
                    option={option}
                    getUpdate={conditionUpdateHandler}
                    defaultValue={condition}
                    groupLabel={t(l.Filters)}
                />
            )}
        </CustomerJourneyVisualizationToggleSection>
    );
};

CustomerJourneyVisualizationReward.displayName = 'CustomerJourneyVisualizationReward';

export default memo(CustomerJourneyVisualizationReward);
